import { useEffect, useContext } from  'react';
import { LiveUpdates, LiveUpdatesChannel } from '@/api/app/live-updates';
import { AppSecretsContext } from '../../components/subcomponents/secrets/app-secrets';
import { AccountInfoContext } from '../../components/subcomponents/account/account-info';
import { useDispatch } from 'react-redux';
import { appendNotification } from '@/redux/app/actions/notification';
import { updateBalanceRemaining } from '@/redux/app/actions/organization';
import { CallsChannel } from '@/api/calls/calls-channel';
import { ReactNative } from '@/api/app/react-native';

export const useSetupV3UserNotificationsChannel = () => {
  const { rememberToken } = useContext(AppSecretsContext);
  const { subdomain } = useContext(AccountInfoContext);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!rememberToken) return;

    window.V3UsersNotificationChannel = new LiveUpdates({
      channel: LiveUpdatesChannel.V3_USERS_NOTIFICATION_CHANNEL,
      token: rememberToken,
      subdomain,
      onConnect: () => console.log('v3 users notification channel connected'),
      onBatchReceived: (events) => {
        events.forEach((event) => {
          const { app_notification } = event;
          dispatch(appendNotification({ appNotification: app_notification }));
        });
      },
    });

    return () => window.V3UsersNotificationChannel.unsubscribe();
  }, [subdomain, rememberToken]);
};

export const useSetupV3OrganizationsChannel = () => {
  const { rememberToken } = useContext(AppSecretsContext);
  const { subdomain } = useContext(AccountInfoContext);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!rememberToken) return;

    window.V3OrganizationsChannel = new LiveUpdates({
      channel: LiveUpdatesChannel.V3_ORGANIZATIONS_CHANNEL,
      token: rememberToken,
      subdomain,
      onConnect: () => console.log('v3 organizations channel connected'),
      onBatchReceived: (events) => {
        events.forEach(({ balance }) => {
          dispatch(updateBalanceRemaining({ balanceRemaining: balance }));
        });
      },
    });

    return () => window.V3OrganizationsChannel.unsubscribe();
  }, [subdomain, rememberToken]);
};

// Conference-ring pipeline (voice_ring): per-account stream of call_ring /
// call_ring_stop events. Only subscribed when the account is on the new pipeline.
//
// Deliberately no effect cleanup: the subscription is page-lifetime and must
// survive this component unmounting on Turbolinks navigations, otherwise ring
// events sent while a Rails page is showing are lost (see @/api/calls/calls-channel).
//
// Never subscribed inside the React Native app: its webview has no web Twilio
// Device (calls run through the native SDK), so it could only claim a call it
// cannot join. Mobile is rung through Twilio's VoIP push into the phone's
// dialer instead (voice_ring's inbound_call ConferenceJob).
export const useSetupV3CallsChannel = () => {
  const { rememberToken } = useContext(AppSecretsContext);
  const { subdomain, id: accountId, conferenceRingEnabled } = useContext(AccountInfoContext);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!rememberToken || !conferenceRingEnabled || ReactNative.exists()) {
      CallsChannel.release();
      return;
    }
    CallsChannel.ensure({ subdomain, accountId, token: rememberToken, dispatch });
  }, [subdomain, accountId, rememberToken, conferenceRingEnabled]);
};